import type { LucideIcon } from "lucide-react";
import { Link, useLocation } from "react-router";
import {
  SidebarGroup,
  SidebarGroupLabel,
  SidebarMenu,
  SidebarMenuButton,
  SidebarMenuItem,
  useSidebar,
} from "@/components/ui/sidebar";

export type NavItem = {
  title: string;
  url: string;
  icon?: LucideIcon;
  alsoActiveWhen?: (pathname: string) => boolean;
};

export type NavGroup = {
  label: string;
  items: NavItem[];
};

export function NavMain({ groups }: { groups: NavGroup[] }) {
  const { pathname } = useLocation();
  const { setOpenMobile } = useSidebar();

  const isActive = (item: NavItem) => {
    if (item.alsoActiveWhen?.(pathname)) return true;
    if (item.url === "/") return pathname === "/";
    // Account overview must not stay active on nested sections.
    if (/^\/accounts\/\d+$/.test(item.url)) {
      return pathname === item.url || pathname === `${item.url}/`;
    }
    // Brand hub must not stay active on plan / kb / materials / apps.
    if (/^\/accounts\/\d+\/brands\/\d+$/.test(item.url)) {
      return pathname === item.url || pathname === `${item.url}/`;
    }
    // Brand management list must not stay active on brand hub / apps.
    if (/\/brands$/.test(item.url)) {
      return pathname === item.url || pathname === `${item.url}/`;
    }
    return pathname === item.url || pathname.startsWith(`${item.url}/`);
  };

  return (
    <>
      {groups.map((group) => (
        <SidebarGroup key={group.label}>
          <SidebarGroupLabel>{group.label}</SidebarGroupLabel>
          <SidebarMenu>
            {group.items.map((item) => (
              <SidebarMenuItem key={item.title}>
                <SidebarMenuButton asChild tooltip={item.title} isActive={isActive(item)}>
                  <Link to={item.url} onClick={() => setOpenMobile(false)}>
                    {item.icon && <item.icon />}
                    <span>{item.title}</span>
                  </Link>
                </SidebarMenuButton>
              </SidebarMenuItem>
            ))}
          </SidebarMenu>
        </SidebarGroup>
      ))}
    </>
  );
}
